import type { AvatarModel, AvatarPolarity } from "@domo/shared"

// Sprint 3/5 — Paleta por firma del avatar. Centralizada acá para que
// el selector, el haz de descenso, los sparkles y el avatar materializado
// lean los mismos colores. Si cambia un modelo, se edita una sola vez.
//
// La firma combina dos ejes:
//
//   · Material (densidad)  : cristal · plasma · biopolimero
//   · Polaridad (estructura): alfa (eléctrico) · omega (magnético)
//
// El material define el color base; la polaridad lo modula:
//   - Alfa: tono más frío y agudo, halo angular, intensidad +0.2.
//   - Omega: tono más cálido y envolvente, halo curvo con undertone
//     rosa/magenta, intensidad -0.1.
//
// - head: la cabeza luminosa que cae
// - beam: el cilindro/cono del haz vertical y el anillo de impacto
// - spark: las partículas tipo Sparkles que estallan al impacto
// - avatarCore: emissive del core del avatar materializado
// - avatarHalo: glow toroidal y aura del avatar
// - emissiveIntensity: cuánto inyectar al Bloom
// - rotationSign: +1 horario (alfa) / -1 antihorario (omega)

export interface ModelPalette {
    head: string
    beam: string
    spark: string
    avatarCore: string
    avatarHalo: string
    emissiveIntensity: number
    rotationSign: 1 | -1
}

const MATERIAL_BASE: Record<AvatarModel, ModelPalette> = {
    cristal: {
        head: "#9CF6FF",
        beam: "#00E5FF",
        spark: "#7CD8FF",
        avatarCore: "#00E5FF",
        avatarHalo: "#7CC5FF",
        emissiveIntensity: 1.6,
        rotationSign: 1,
    },
    plasma: {
        head: "#FFE0A0",
        beam: "#FFB347",
        spark: "#FFD18C",
        avatarCore: "#FF9C3D",
        avatarHalo: "#FFD58A",
        emissiveIntensity: 1.9,
        rotationSign: 1,
    },
    biopolimero: {
        head: "#E2C8FF",
        beam: "#B57BFF",
        spark: "#D4ADFF",
        avatarCore: "#9856FF",
        avatarHalo: "#D6B0FF",
        emissiveIntensity: 1.4,
        rotationSign: 1,
    },
}

// Modulación por polaridad. Mezcla en HSL para no perder identidad del
// material — solo desplaza el hue ligeramente y ajusta saturación.
const POLARITY_MOD: Record<
    AvatarPolarity,
    { hueShift: number; sat: number; intensityDelta: number; rotationSign: 1 | -1 }
> = {
    alfa: { hueShift: -8, sat: 1.05, intensityDelta: 0.2, rotationSign: 1 },
    omega: { hueShift: 14, sat: 0.95, intensityDelta: -0.1, rotationSign: -1 },
}

export function modelPalette(
    model: AvatarModel,
    polarity: AvatarPolarity | null = null
): ModelPalette {
    const base = MATERIAL_BASE[model]
    if (!polarity) return base
    const mod = POLARITY_MOD[polarity]
    return {
        head: shiftHueHex(base.head, mod.hueShift, mod.sat),
        beam: shiftHueHex(base.beam, mod.hueShift, mod.sat),
        spark: shiftHueHex(base.spark, mod.hueShift, mod.sat),
        avatarCore: shiftHueHex(base.avatarCore, mod.hueShift, mod.sat),
        avatarHalo: shiftHueHex(base.avatarHalo, mod.hueShift, mod.sat),
        emissiveIntensity: Math.max(
            0.6,
            base.emissiveIntensity + mod.intensityDelta
        ),
        rotationSign: mod.rotationSign,
    }
}

// ── HSL utilities (locales) ──────────────────────────────
// Desplazamientos pequeños de hue/sat son suficientes para sentir la
// polaridad sin perder la identidad del material. Implementación inline
// para no agregar deps.
function shiftHueHex(hex: string, hueDeg: number, satMul: number): string {
    const { h, s, l } = hexToHsl(hex)
    const h2 = ((h + hueDeg) % 360 + 360) % 360
    const s2 = Math.max(0, Math.min(1, s * satMul))
    return hslToHex(h2, s2, l)
}

function hexToHsl(hex: string): { h: number; s: number; l: number } {
    const v = hex.replace("#", "")
    const full =
        v.length === 3 ? v.split("").map((c) => c + c).join("") : v
    const r = parseInt(full.slice(0, 2), 16) / 255
    const g = parseInt(full.slice(2, 4), 16) / 255
    const b = parseInt(full.slice(4, 6), 16) / 255
    const max = Math.max(r, g, b)
    const min = Math.min(r, g, b)
    const l = (max + min) / 2
    if (max === min) return { h: 0, s: 0, l }
    const d = max - min
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    let h: number
    switch (max) {
        case r:
            h = ((g - b) / d + (g < b ? 6 : 0)) * 60
            break
        case g:
            h = ((b - r) / d + 2) * 60
            break
        default:
            h = ((r - g) / d + 4) * 60
    }
    return { h, s, l }
}

function hslToHex(h: number, s: number, l: number): string {
    const c = (1 - Math.abs(2 * l - 1)) * s
    const hp = h / 60
    const x = c * (1 - Math.abs((hp % 2) - 1))
    let r = 0,
        g = 0,
        b = 0
    if (hp < 1) [r, g, b] = [c, x, 0]
    else if (hp < 2) [r, g, b] = [x, c, 0]
    else if (hp < 3) [r, g, b] = [0, c, x]
    else if (hp < 4) [r, g, b] = [0, x, c]
    else if (hp < 5) [r, g, b] = [x, 0, c]
    else [r, g, b] = [c, 0, x]
    const m = l - c / 2
    const to8 = (n: number) =>
        Math.max(0, Math.min(255, Math.round((n + m) * 255)))
    const hex = (n: number) => to8(n).toString(16).padStart(2, "0")
    return `#${hex(r)}${hex(g)}${hex(b)}`
}
